/*
 * 3D Collection — dark 3D arc/coverflow product showcase.
 * Standalone, namespaced under window.P3D. No dependency on any other
 * section's JS/CSS. Vanilla drag physics (no external library): a small
 * hand-rolled spring integrator drives momentum + snap-to-center, so the
 * whole thing is one lightweight self-contained script.
 *
 * Rendering is transform/opacity/filter only (compositor-friendly, no
 * layout thrash) and the physics loop only runs while dragging or while
 * still settling — it does not run continuously at idle.
 */
(function () {
  if (window.P3D && window.P3D.__initialized) return;
  window.P3D = window.P3D || {};
  window.P3D.__initialized = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var CONFIG = {
    desktop: { spacing: 190, depth: 130, angle: 24, maxAngle: 62, scaleStep: 0.15, minScale: 0.52, opacityStep: 0.24, minOpacity: 0.05, blurStep: 0.7, maxBlur: 2.4, glowMax: 26, glowMin: 4 },
    mobile: { spacing: 104, depth: 78, angle: 20, maxAngle: 56, scaleStep: 0.18, minScale: 0.46, opacityStep: 0.28, minOpacity: 0.04, blurStep: 0.8, maxBlur: 2, glowMax: 18, glowMin: 3 }
  };
  var SPRING_K = 0.14;
  var SPRING_DAMPING = 0.8;
  var CLICK_THRESHOLD = 6; // px of movement below which a pointerup counts as a click
  var SETTLE_EPS = 0.0015;

  function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }

  function isMobile() { return window.matchMedia('(max-width: 749px)').matches; }

  function createInstance(root) {
    var stage = root.querySelector('.p3d-stage');
    var track = root.querySelector('.p3d-track');
    if (!stage || !track) return null;

    var itemEls = Array.prototype.slice.call(track.querySelectorAll('.p3d-item'));
    if (!itemEls.length) return null;

    var items = itemEls.map(function (el) {
      return { el: el, glow: el.style.getPropertyValue('--p3d-glow') || '#6cf2d0' };
    });

    var progress = root.querySelector('.p3d-progress');
    var progressThumb = root.querySelector('.p3d-progress__thumb');

    var activeScale = parseFloat(stage.dataset.activeScale);
    if (!isFinite(activeScale)) activeScale = 1;
    var sideScale = parseFloat(stage.dataset.sideScale);
    if (!isFinite(sideScale)) sideScale = CONFIG.desktop.minScale;

    var count = items.length;
    var state = {
      pos: 0,
      vel: 0,
      target: 0,
      dragging: false,
      moved: false,
      suppressClick: false,
      startX: 0,
      startPos: 0,
      samples: [],
      raf: null
    };

    function cfg() { return isMobile() ? CONFIG.mobile : CONFIG.desktop; }

    function render() {
      var c = cfg();
      var pos = state.pos;
      var nearest = clamp(Math.round(pos), 0, count - 1);

      items.forEach(function (item, i) {
        var offset = i - pos;
        var abs = Math.abs(offset);
        var rot = clamp(offset * c.angle, -c.maxAngle, c.maxAngle);
        var scale = Math.max(sideScale, activeScale - abs * c.scaleStep);
        var opacity = Math.max(c.minOpacity, 1 - abs * c.opacityStep);
        var tz = -abs * c.depth;
        var tx = offset * c.spacing;
        var blur = Math.min(c.maxBlur, abs * c.blurStep);
        var glowR = Math.max(c.glowMin, c.glowMax - abs * (c.glowMax * 0.55));
        var z = Math.round(1000 - abs * 10);

        var el = item.el;
        el.style.transform =
          'translate(-50%, -50%) translateX(' + tx.toFixed(2) + 'px) translateZ(' + tz.toFixed(2) + 'px) rotateY(' + rot.toFixed(2) + 'deg) scale(' + scale.toFixed(3) + ')';
        el.style.opacity = opacity.toFixed(3);
        el.style.zIndex = z;
        el.style.setProperty('--p3d-blur', blur.toFixed(2) + 'px');
        el.style.setProperty('--p3d-glow-r', glowR.toFixed(1) + 'px');
        el.style.pointerEvents = opacity < 0.06 ? 'none' : '';

        var active = i === nearest;
        if (el.classList.contains('is-active') !== active) {
          el.classList.toggle('is-active', active);
          el.setAttribute('aria-selected', active ? 'true' : 'false');
          el.tabIndex = active || abs < 2 ? 0 : -1;
        }
      });

      var activeGlow = items[nearest] ? items[nearest].glow : null;
      if (activeGlow) stage.style.setProperty('--p3d-active-glow', activeGlow);

      if (progressThumb && progress) {
        var trackW = progress.clientWidth;
        var thumbW = count ? trackW / count : trackW;
        var pct = count > 1 ? pos / (count - 1) : 0;
        var left = clamp(pct, 0, 1) * (trackW - thumbW);
        progressThumb.style.width = thumbW + 'px';
        progressThumb.style.transform = 'translateX(' + left.toFixed(2) + 'px)';
      }
    }

    function stopLoop() {
      if (state.raf) { cancelAnimationFrame(state.raf); state.raf = null; }
    }

    function startLoop() {
      if (state.raf) return;
      state.raf = requestAnimationFrame(tick);
    }

    function tick() {
      if (state.dragging) {
        render();
        state.raf = requestAnimationFrame(tick);
        return;
      }
      if (reduceMotion) {
        state.pos = state.target;
        state.vel = 0;
        render();
        state.raf = null;
        return;
      }
      var diff = state.target - state.pos;
      state.vel += diff * SPRING_K;
      state.vel *= SPRING_DAMPING;
      state.pos += state.vel;
      render();
      if (Math.abs(diff) < SETTLE_EPS && Math.abs(state.vel) < SETTLE_EPS) {
        state.pos = state.target;
        state.vel = 0;
        render();
        state.raf = null;
        return;
      }
      state.raf = requestAnimationFrame(tick);
    }

    function setTarget(index, initialVel) {
      state.target = clamp(index, 0, count - 1);
      if (typeof initialVel === 'number') state.vel = initialVel;
      startLoop();
    }

    function activate(index) {
      setTarget(index, 0);
    }

    /* ------------------------------ pointer/drag ------------------------------ */
    function onPointerDown(e) {
      if (e.button !== undefined && e.button !== 0) return;
      state.dragging = true;
      state.moved = false;
      state.startX = e.clientX;
      state.startPos = state.pos;
      state.vel = 0;
      state.samples = [{ x: e.clientX, t: e.timeStamp }];
      try { track.setPointerCapture(e.pointerId); } catch (err) {}
      startLoop();
    }

    function onPointerMove(e) {
      if (!state.dragging) return;
      var dx = e.clientX - state.startX;
      if (Math.abs(dx) > CLICK_THRESHOLD) state.moved = true;
      var spacing = cfg().spacing;
      state.pos = clamp(state.startPos - dx / spacing, 0, count - 1);
      state.samples.push({ x: e.clientX, t: e.timeStamp });
      if (state.samples.length > 6) state.samples.shift();
    }

    function onPointerUp(e) {
      if (!state.dragging) return;
      state.dragging = false;

      if (!state.moved) {
        // treat as a click: activate item under pointer, if any
        var el = e.target && e.target.closest ? e.target.closest('.p3d-item') : null;
        if (el) {
          var idx = itemEls.indexOf(el);
          if (idx !== -1) {
            state.suppressClick = true;
            requestAnimationFrame(function () { state.suppressClick = false; });
            activate(idx);
            return;
          }
        }
        // no item under pointer — settle back to nearest (no-op if already integer)
        setTarget(Math.round(state.pos), 0);
        return;
      }

      state.suppressClick = true;
      requestAnimationFrame(function () { state.suppressClick = false; });

      // velocity from recent samples (px/ms) -> index-units/frame, with lookahead
      var samples = state.samples;
      var vel = 0;
      if (samples.length >= 2) {
        var a = samples[0], b = samples[samples.length - 1];
        var dt = b.t - a.t;
        if (dt > 0) {
          var pxPerMs = (b.x - a.x) / dt;
          vel = -(pxPerMs / cfg().spacing) * 16.67; // index-units per ~frame
        }
      }
      vel = clamp(vel, -1.6, 1.6);
      var predicted = Math.round(state.pos + vel * 5);
      setTarget(predicted, vel);
    }

    function onClick(e) {
      if (state.suppressClick) return;
      var el = e.target && e.target.closest ? e.target.closest('.p3d-item') : null;
      if (!el) return;
      var idx = itemEls.indexOf(el);
      if (idx !== -1) activate(idx);
    }

    function onKeydown(e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); activate(Math.round(state.pos) + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); activate(Math.round(state.pos) - 1); }
      else if (e.key === 'Home') { e.preventDefault(); activate(0); }
      else if (e.key === 'End') { e.preventDefault(); activate(count - 1); }
    }

    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', onPointerUp);
    track.addEventListener('pointercancel', onPointerUp);
    track.addEventListener('click', onClick);
    track.addEventListener('keydown', onKeydown);

    var resizeTimer;
    function onResize() { render(); }
    window.addEventListener('resize', onResize);
    if (typeof ResizeObserver !== 'undefined' && progress) {
      var ro = new ResizeObserver(function () { render(); });
      ro.observe(progress);
    }

    render();

    return {
      destroy: function () {
        stopLoop();
        track.removeEventListener('pointerdown', onPointerDown);
        track.removeEventListener('pointermove', onPointerMove);
        track.removeEventListener('pointerup', onPointerUp);
        track.removeEventListener('pointercancel', onPointerUp);
        track.removeEventListener('click', onClick);
        track.removeEventListener('keydown', onKeydown);
        window.removeEventListener('resize', onResize);
      }
    };
  }

  var registry = new Map(); // root el -> instance

  function initRoot(root) {
    if (!root || registry.has(root)) return;
    var inst = createInstance(root);
    if (inst) registry.set(root, inst);
  }

  function destroyRoot(root) {
    var inst = registry.get(root);
    if (inst) { inst.destroy(); registry.delete(root); }
  }

  function initAll(scope) {
    scope = scope || document;
    var roots = [];
    if (scope !== document && scope.matches && scope.matches('.p3d-root')) roots.push(scope);
    if (scope.querySelectorAll) roots = roots.concat(Array.prototype.slice.call(scope.querySelectorAll('.p3d-root')));
    roots.forEach(initRoot);
  }

  function boot() {
    initAll(document);
    document.addEventListener('shopify:section:unload', function (e) {
      var root = e.target.querySelector ? e.target.querySelector('.p3d-root') : null;
      if (root) destroyRoot(root);
    });
    document.addEventListener('shopify:section:load', function (e) {
      initAll(e.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
